/**
 * React hooks for Memory Intelligence using React Query
 */

import { useQuery, useMutation, UseQueryOptions, UseMutationOptions } from "@tanstack/react-query";
import { useMemoryIntelligenceContext } from "../context/MemoryIntelligenceProvider.js";
import {
  PatternAnalysis,
  TagSuggestionsResult,
  RelatedMemoriesResult,
  DuplicatesResult,
  InsightsResult,
  MemoryHealth,
  AnalyzePatternsParams,
  SuggestTagsParams,
  FindRelatedParams,
  DetectDuplicatesParams,
  ExtractInsightsParams,
  HealthCheckParams,
} from "../../core/types.js";
import {
  PredictiveRecallParams,
  PredictiveRecallResult,
} from "../../core/prediction-types.js";
import type { IntelligenceResponse } from "../../core/client.js";

/**
 * Get the Memory Intelligence client from context
 */
export function useMemoryIntelligence() {
  const { client } = useMemoryIntelligenceContext();
  return client;
}

/**
 * Hook for analyzing memory patterns
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = usePatternAnalysis({
 *   userId: "user-123",
 *   timeRangeDays: 30,
 *   responseFormat: "json"
 * });
 * ```
 */
export function usePatternAnalysis(
  params: AnalyzePatternsParams,
  options?: Omit<UseQueryOptions<IntelligenceResponse<PatternAnalysis>>, "queryKey" | "queryFn">
) {
  const client = useMemoryIntelligence();

  return useQuery<IntelligenceResponse<PatternAnalysis>>({
    queryKey: ["memory-intelligence", "analyze-patterns", params],
    queryFn: () => client.analyzePatterns(params),
    ...options,
  });
}

/**
 * Hook for getting tag suggestions
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useTagSuggestions({
 *   memoryId: "mem-123",
 *   userId: "user-123",
 *   maxSuggestions: 5
 * });
 * ```
 */
export function useTagSuggestions(
  params: SuggestTagsParams,
  options?: Omit<UseQueryOptions<IntelligenceResponse<TagSuggestionsResult>>, "queryKey" | "queryFn">
) {
  const client = useMemoryIntelligence();

  return useQuery<IntelligenceResponse<TagSuggestionsResult>>({
    queryKey: ["memory-intelligence", "suggest-tags", params],
    queryFn: () => client.suggestTags(params),
    ...options,
  });
}

/**
 * Mutation hook for getting tag suggestions (if you need manual trigger)
 *
 * @example
 * ```tsx
 * const { mutate, data, isPending } = useTagSuggestionsMutation();
 *
 * // Later...
 * mutate({
 *   memoryId: "mem-123",
 *   userId: "user-123"
 * });
 * ```
 */
export function useTagSuggestionsMutation(
  options?: UseMutationOptions<IntelligenceResponse<TagSuggestionsResult>, Error, SuggestTagsParams>
) {
  const client = useMemoryIntelligence();

  return useMutation<IntelligenceResponse<TagSuggestionsResult>, Error, SuggestTagsParams>({
    mutationFn: (params) => client.suggestTags(params),
    ...options,
  });
}

/**
 * Hook for finding related memories
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useRelatedMemories({
 *   memoryId: "mem-123",
 *   userId: "user-123",
 *   limit: 10,
 *   similarityThreshold: 0.7
 * });
 * ```
 */
export function useRelatedMemories(
  params: FindRelatedParams,
  options?: Omit<UseQueryOptions<IntelligenceResponse<RelatedMemoriesResult>>, "queryKey" | "queryFn">
) {
  const client = useMemoryIntelligence();

  return useQuery<IntelligenceResponse<RelatedMemoriesResult>>({
    queryKey: ["memory-intelligence", "find-related", params],
    queryFn: () => client.findRelated(params),
    ...options,
  });
}

/**
 * Hook for detecting duplicate memories
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useDuplicateDetection({
 *   userId: "user-123",
 *   similarityThreshold: 0.9,
 *   maxPairs: 20
 * });
 * ```
 */
export function useDuplicateDetection(
  params: DetectDuplicatesParams,
  options?: Omit<UseQueryOptions<IntelligenceResponse<DuplicatesResult>>, "queryKey" | "queryFn">
) {
  const client = useMemoryIntelligence();

  return useQuery<IntelligenceResponse<DuplicatesResult>>({
    queryKey: ["memory-intelligence", "detect-duplicates", params],
    queryFn: () => client.detectDuplicates(params),
    ...options,
  });
}

/**
 * Mutation hook for duplicate detection (if you need manual trigger)
 */
export function useDuplicateDetectionMutation(
  options?: UseMutationOptions<IntelligenceResponse<DuplicatesResult>, Error, DetectDuplicatesParams>
) {
  const client = useMemoryIntelligence();

  return useMutation<IntelligenceResponse<DuplicatesResult>, Error, DetectDuplicatesParams>({
    mutationFn: (params) => client.detectDuplicates(params),
    ...options,
  });
}

/**
 * Hook for extracting insights
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useInsightExtraction({
 *   userId: "user-123",
 *   topic: "machine learning",
 *   maxMemories: 20
 * });
 * ```
 */
export function useInsightExtraction(
  params: ExtractInsightsParams,
  options?: Omit<UseQueryOptions<IntelligenceResponse<InsightsResult>>, "queryKey" | "queryFn">
) {
  const client = useMemoryIntelligence();

  return useQuery<IntelligenceResponse<InsightsResult>>({
    queryKey: ["memory-intelligence", "extract-insights", params],
    queryFn: () => client.extractInsights(params),
    ...options,
  });
}

/**
 * Mutation hook for insight extraction (if you need manual trigger)
 */
export function useInsightExtractionMutation(
  options?: UseMutationOptions<IntelligenceResponse<InsightsResult>, Error, ExtractInsightsParams>
) {
  const client = useMemoryIntelligence();

  return useMutation<IntelligenceResponse<InsightsResult>, Error, ExtractInsightsParams>({
    mutationFn: (params) => client.extractInsights(params),
    ...options,
  });
}

/**
 * Hook for checking memory health
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useHealthCheck({
 *   userId: "user-123",
 *   responseFormat: "json"
 * });
 * ```
 */
export function useHealthCheck(
  params: HealthCheckParams,
  options?: Omit<UseQueryOptions<IntelligenceResponse<MemoryHealth>>, "queryKey" | "queryFn">
) {
  const client = useMemoryIntelligence();

  return useQuery<IntelligenceResponse<MemoryHealth>>({
    queryKey: ["memory-intelligence", "health-check", params],
    queryFn: () => client.healthCheck(params),
    ...options,
  });
}

/**
 * Mutation hook for health check (if you need manual trigger)
 */
export function useHealthCheckMutation(
  options?: UseMutationOptions<IntelligenceResponse<MemoryHealth>, Error, HealthCheckParams>
) {
  const client = useMemoryIntelligence();

  return useMutation<IntelligenceResponse<MemoryHealth>, Error, HealthCheckParams>({
    mutationFn: (params) => client.healthCheck(params),
    ...options,
  });
}

/**
 * Hook for predictive memory recall
 *
 * AI-powered prediction of memories you'll need based on your current context.
 * Uses semantic similarity, temporal relevance, usage patterns, and serendipity
 * scoring to anticipate what you need before you realize it.
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = usePredictiveRecall({
 *   userId: "user-123",
 *   context: {
 *     currentProject: "Building dashboard components",
 *     recentTopics: ["React", "performance"],
 *     contextText: "Optimizing render performance"
 *   },
 *   limit: 5,
 *   minConfidence: 50
 * });
 *
 * // Display predictions
 * {data?.data.predictions.map(pred => (
 *   <PredictionCard
 *     key={pred.id}
 *     title={pred.title}
 *     confidence={pred.confidence}
 *     reason={pred.reason}
 *     action={pred.suggestedAction}
 *   />
 * ))}
 * ```
 */
export function usePredictiveRecall(
  params: PredictiveRecallParams,
  options?: Omit<UseQueryOptions<IntelligenceResponse<PredictiveRecallResult>>, "queryKey" | "queryFn">
) {
  const client = useMemoryIntelligence();

  return useQuery<IntelligenceResponse<PredictiveRecallResult>>({
    queryKey: ["memory-intelligence", "predictive-recall", params],
    queryFn: () => client.predictiveRecall(params),
    // Predictions should refresh periodically as context changes
    staleTime: 60000, // 1 minute
    ...options,
  });
}

/**
 * Mutation hook for predictive recall (if you need manual trigger)
 *
 * @example
 * ```tsx
 * const { mutate, data, isPending } = usePredictiveRecallMutation();
 *
 * // Trigger prediction when context changes
 * const handleContextChange = (newContext) => {
 *   mutate({
 *     userId: "user-123",
 *     context: newContext,
 *     limit: 5
 *   });
 * };
 * ```
 */
export function usePredictiveRecallMutation(
  options?: UseMutationOptions<IntelligenceResponse<PredictiveRecallResult>, Error, PredictiveRecallParams>
) {
  const client = useMemoryIntelligence();

  return useMutation<IntelligenceResponse<PredictiveRecallResult>, Error, PredictiveRecallParams>({
    mutationFn: (params) => client.predictiveRecall(params),
    ...options,
  });
}

/**
 * Hook for recording prediction feedback
 *
 * @example
 * ```tsx
 * const { mutate: recordFeedback } = usePredictionFeedback();
 *
 * // When user clicks on a prediction
 * const handlePredictionClick = (memoryId: string) => {
 *   recordFeedback({
 *     memoryId,
 *     userId: "user-123",
 *     useful: true,
 *     action: "clicked"
 *   });
 * };
 * ```
 */
export function usePredictionFeedback(
  options?: UseMutationOptions<void, Error, {
    memoryId: string;
    userId: string;
    useful: boolean;
    action: "clicked" | "saved" | "dismissed" | "ignored";
    dismissReason?: "not_relevant" | "already_know" | "not_now" | "other";
  }>
) {
  const client = useMemoryIntelligence();

  return useMutation<void, Error, {
    memoryId: string;
    userId: string;
    useful: boolean;
    action: "clicked" | "saved" | "dismissed" | "ignored";
    dismissReason?: "not_relevant" | "already_know" | "not_now" | "other";
  }>({
    mutationFn: (params) => client.recordPredictionFeedback(params),
    ...options,
  });
}
